import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import path from 'path';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import FileListItem from './FileListItem';
import { getFileTypeImage } from '../../../util/fileTypeImages';
import '../../../styles/filelist.scss';

const portal = document.createElement('div');
portal.classList.add('file-list');
document.body.appendChild(portal);

const defaultMessage = 'No files to display';

const getIcon = file => {
  const extension = path.extname(file.path).substring(1) || path.extname(file.originalname).substring(1);
  return getFileTypeImage(extension);
};

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const FileList = ({
  disableDrag = false,
  emptyMessage = defaultMessage,
  files = [],
  onClick = () => {},
  readOnly = false,
  removeFile = () => {},
  reorderFiles = () => {},
  selectAttachment = () => {},
  ...rest
}) => {
  const [list, setList] = useState(files);

  const onDragEnd = result => {
    if (!result.destination) {
      return;
    }

    if (result.destination.index === result.source.index) {
      return;
    }

    const reOrderedList = reorder(list, result.source.index, result.destination.index);

    setList(reOrderedList);
    reorderFiles(reOrderedList);
  };

  const stop = e => {
    e.stopPropagation();
    e.preventDefault();
  };

  useEffect(() => {
    setList(files);
  }, [files]);

  if (!files.length) {
    return <div className="empty-message">{emptyMessage}</div>;
  }

  if (disableDrag) {
    return (
      <div className="file-list" {...rest}>
        {list.map(file => {
          const icon = getIcon(file);
          const key = String(file.key || file.id);

          return (
            <FileListItem
              canDelete={!readOnly}
              disableDrag
              file={file}
              icon={icon}
              key={key}
              onClick={onClick}
              onDelete={removeFile}
              onSelect={selectAttachment}
            />
          );
        })}
      </div>
    );
  }

  return (
    <div onMouseUp={stop} onClick={stop}>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="list">
          {provided => (
            <div ref={provided.innerRef} {...provided.droppableProps} className="file-list" {...rest}>
              {list.map((file, index) => {
                const icon = getIcon(file);
                const key = String(file.key || file.id);

                return (
                  <Draggable key={key} draggableId={key} index={index}>
                    {(provided, snapshot) => {
                      const child = (
                        <FileListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          key={key}
                          file={file}
                          icon={icon}
                          canDelete={!readOnly}
                          onClick={onClick}
                          onDelete={removeFile}
                          onSelect={selectAttachment}
                        />
                      );

                      if (snapshot.isDragging) {
                        return ReactDOM.createPortal(child, portal);
                      } else {
                        return child;
                      }
                    }}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </div>
  );
};

FileList.propTypes = {
  emptyMessage: PropTypes.string,
  files: PropTypes.array,
  onClick: PropTypes.func,
  readOnly: PropTypes.bool,
  removeFile: PropTypes.func,
  reorderFiles: PropTypes.func,
};

export default FileList;
